import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  computed,
  signal,
  viewChild,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  AppSvgIconComponent,
  BaseOverlayComponent,
  SignalButton,
  SignalTextField,
} from '@vallift/ngl';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import {
  PublicFormsApiService,
  type PublicFormAuthOptions,
} from '@core/api/public/public-forms-api.service';
import {
  fetchSearchablePickerPage,
  SEARCHABLE_PICKER_PAGE_SIZE,
  type CollectionSelectOption,
} from '../../utils/resolve-collection-dropdown-items';
import type { CollectionSelectAnswer } from '../../utils/respondent-answer-form';
import { map, Subscription } from 'rxjs';

export interface SearchablePickerOverlayData {
  title?: string;
  collectionId: string;
  primaryKey: string;
  secondaryKey: string;
  selectedId?: string | null;
  pageSize?: number;
  /** When set, load options via public form API instead of workspace JWT. */
  publicField?: {
    workspaceSlug: string;
    formSlug: string;
    fieldId: string;
    auth: PublicFormAuthOptions;
  };
}

const SCROLL_LOAD_THRESHOLD_PX = 120;

@Component({
  selector: 'app-searchable-picker-overlay',
  standalone: true,
  imports: [
    BaseOverlayComponent,
    SignalButton,
    SignalTextField,
  ],
  templateUrl: './searchable-picker-overlay.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchablePickerOverlayComponent implements OnInit, OnDestroy {
  private readonly dialogRef = inject(
    DialogRef<CollectionSelectAnswer | null | undefined>,
  );
  private readonly dialogData = inject<SearchablePickerOverlayData>(DIALOG_DATA);
  private readonly workspaceApi = inject(WorkspaceAPIService);
  private readonly publicFormsApi = inject(PublicFormsApiService);
  private readonly injector = inject(Injector);

  private readonly items = signal<CollectionSelectOption[]>([]);
  private readonly pageNumber = signal(1);
  private readonly hasMore = signal(false);
  private readonly loading = signal(false);
  private readonly loadingMore = signal(false);
  readonly search = signal('');
  private uniqueFieldKey = '';
  private loadSub: Subscription | null = null;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private observer: IntersectionObserver | null = null;

  private readonly listRoot =
    viewChild<ElementRef<HTMLElement>>('listRoot');
  private readonly sentinel = viewChild<ElementRef<HTMLElement>>('sentinel');

  readonly title = computed(
    () => this.dialogData.title?.trim() || 'Choose an item',
  );
  readonly isLoading = computed(() => this.loading());
  readonly isLoadingMore = computed(() => this.loadingMore());
  readonly isEmpty = computed(
    () => !this.loading() && this.items().length === 0,
  );
  readonly listItems = computed(() => this.items());
  readonly selectedId = computed(
    () => this.dialogData.selectedId?.trim() || '',
  );
  readonly canClear = computed(() => Boolean(this.selectedId()));

  ngOnInit(): void {
    this.loadPage(1, true);
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.observer?.disconnect();
  }

  onSearchChange(value: string): void {
    this.search.set(value);
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.searchTimer = setTimeout(() => {
      this.loadPage(1, true);
    }, 300);
  }

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement | null;
    if (!el) {
      return;
    }
    const nearBottom =
      el.scrollTop + el.clientHeight >=
      el.scrollHeight - SCROLL_LOAD_THRESHOLD_PX;
    if (nearBottom) {
      this.loadNextPage();
    }
  }

  selectItem(item: CollectionSelectOption): void {
    this.dialogRef.close({
      id: item.id,
      uniqueKey: item.uniqueKey,
      display: item.display,
      ...(item.secondaryDisplay
        ? { secondaryDisplay: item.secondaryDisplay }
        : {}),
    });
  }

  clearSelection(): void {
    this.dialogRef.close(null);
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  private scheduleScrollSetup(): void {
    afterNextRender(
      () => {
        this.setupObserver();
        this.tryLoadIfListNotScrollable();
      },
      { injector: this.injector },
    );
  }

  /** When the first page does not fill the scroll area, load more until it does or no more pages. */
  private tryLoadIfListNotScrollable(): void {
    const root = this.listRoot()?.nativeElement;
    if (!root || this.loading() || this.loadingMore() || !this.hasMore()) {
      return;
    }
    if (root.scrollHeight <= root.clientHeight + SCROLL_LOAD_THRESHOLD_PX) {
      this.loadNextPage();
    }
  }

  private setupObserver(): void {
    this.observer?.disconnect();
    const el = this.sentinel()?.nativeElement;
    const root = this.listRoot()?.nativeElement;
    if (!el || !root || typeof IntersectionObserver === 'undefined') {
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadNextPage();
        }
      },
      { root, rootMargin: '120px', threshold: 0 },
    );
    this.observer.observe(el);
  }

  private loadNextPage(): void {
    if (!this.hasMore() || this.loading() || this.loadingMore()) {
      return;
    }
    this.loadPage(this.pageNumber() + 1, false);
  }

  private loadPage(pageNumber: number, replace: boolean): void {
    const publicField = this.dialogData.publicField;
    const collectionId = this.dialogData.collectionId?.trim();
    const primaryKey = this.dialogData.primaryKey?.trim();
    const secondaryKey = this.dialogData.secondaryKey?.trim();
    if (!publicField && (!collectionId || !primaryKey || !secondaryKey)) {
      this.items.set([]);
      this.hasMore.set(false);
      return;
    }

    this.loadSub?.unsubscribe();
    if (replace) {
      this.loading.set(true);
    } else {
      this.loadingMore.set(true);
    }

    const pageSize = this.dialogData.pageSize ?? SEARCHABLE_PICKER_PAGE_SIZE;

    const request$ = publicField
      ? this.publicFormsApi
          .listFieldOptions(
            publicField.workspaceSlug,
            publicField.formSlug,
            publicField.fieldId,
            { q: this.search(), limit: pageSize },
            publicField.auth,
          )
          .pipe(
            map((response) => {
              const items: CollectionSelectOption[] = (
                response.data?.options ?? []
              ).map((option) => ({
                id: option.id,
                uniqueKey: option.uniqueKey?.trim() || option.id,
                display: option.label,
                ...(option.secondaryLabel
                  ? { secondaryDisplay: option.secondaryLabel }
                  : {}),
              }));
              return {
                items,
                totalCount: response.data?.totalCount ?? items.length,
                pageNumber: 1,
                pageSize,
                hasMore: false,
                uniqueFieldKey: this.uniqueFieldKey,
              };
            }),
          )
      : fetchSearchablePickerPage(this.workspaceApi, {
          collectionId: collectionId!,
          primaryKey: primaryKey!,
          secondaryKey: secondaryKey!,
          search: this.search(),
          pageNumber,
          pageSize,
          ...(this.uniqueFieldKey ? { uniqueFieldKey: this.uniqueFieldKey } : {}),
        });

    this.loadSub = request$.subscribe({
      next: (page) => {
        this.uniqueFieldKey = page.uniqueFieldKey || this.uniqueFieldKey;
        this.pageNumber.set(page.pageNumber);
        this.hasMore.set(page.hasMore);
        this.items.set(
          replace ? page.items : [...this.items(), ...page.items],
        );
        this.loading.set(false);
        this.loadingMore.set(false);
        this.scheduleScrollSetup();
      },
      error: () => {
        if (replace) {
          this.items.set([]);
          this.hasMore.set(false);
        }
        this.loading.set(false);
        this.loadingMore.set(false);
      },
    });
  }
}
